import React, { useEffect, useState } from 'react';
import type { ModalComponentProps } from '@components/modals/constants';
import { Button } from '@components/primitives/Button/Button';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useAppointmentTypes } from '../store/hooks';
import { type AppointmentType } from '../types';

import AppointmentTypeModal from './AppointmentTypeModal';
import { ColorDot } from './ColorDot';

/**
 * Management modal for viewing, editing, and creating appointment types
 * Displays a list of all appointment types with ability to:
 * - View all types with their colors
 * - Click to edit existing types
 * - Create new custom types
 */
export const AppointmentTypesManagementModal: React.FC<ModalComponentProps> = ({ closeModal }) => {
  const { appointmentTypes, isLoading } = useAppointmentTypes();
  const { appointment } = useEventTracking();

  // State for edit/create modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);

  // Track modal opened on mount
  useEffect(() => {
    appointment.typesManagementModalOpened({
      source: 'calendar_sidebar',
      appointment_types_count: appointmentTypes.length,
    });
  }, [appointment, appointmentTypes.length]);

  const handleEditType = (type: AppointmentType) => {
    setSelectedType(type);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedType(null);
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  return (
    <>
      {/* Header */}
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: 1, color: 'primary.main' }}
          >
            APPOINTMENT TYPE
          </Typography>
          <IconButton
            onClick={closeModal}
            size="small"
            sx={{ color: 'text.secondary' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content - Scrollable list of appointment types */}
      <DialogContent sx={{ p: 0, pb: 2 }}>
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading appointment types...</Typography>
          </Box>
        ) : appointmentTypes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No appointment types found. Create your first one below.
            </Typography>
          </Box>
        ) : (
          <Box>
            {appointmentTypes.map(type => (
              <Box key={type.encounter_type_id}>
                <Box
                  onClick={() => handleEditType(type)}
                  sx={{
                    py: 1,
                    px: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: '#F4F1F8',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 500 }}>{type.encounter_type}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {type.default_duration && (
                      <Typography variant="caption" color="text.secondary">
                        {type.default_duration}min
                      </Typography>
                    )}
                    <ColorDot colorEnum={type.color} size={16} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      {/* Footer with Create button */}
      <DialogActions sx={{ p: 3, justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="primary" onClick={handleOpenCreateModal}>
          Create a New Appointment Type
        </Button>
      </DialogActions>

      {/* Edit Modal */}
      {showEditModal && selectedType && (
        <AppointmentTypeModal
          open={showEditModal}
          onClose={handleCloseEditModal}
          type="edit"
          appointmentType={selectedType}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <AppointmentTypeModal
          open={showCreateModal}
          onClose={handleCloseCreateModal}
          type="create"
        />
      )}
    </>
  );
};

export default AppointmentTypesManagementModal;
